import React, { useState } from 'react';
import { Store, Truck, Users, TrendingUp, CheckCircle2, ArrowRight, BarChart3, Megaphone, MapPin, Handshake, Gift, Headphones, Target, LineChart, AlertCircle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const PartnersSection = () => {
  const { t, direction, language } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    city: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg('');
    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/partner`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Erreur lors de l\'envoi');
      }
      setSubmitted(true);
      setFormData({ name: '', email: '', phone: '', city: '', message: '' });
      setTimeout(() => setSubmitted(false), 6000);
    } catch (err) {
      setErrorMsg(err.message || 'Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setSubmitting(false);
    }
  };

  const content = {
    fr: {
      badge: "PARTENARIAT",
      title: "Devenez Partenaire Olion",
      subtitle: "Rejoignez notre réseau de 200+ partenaires à travers la Tunisie",
      conceptTitle: "Concept de Partenariat",
      conceptDesc: "Un modèle gagnant-gagnant avec des marges attractives et un accompagnement personnalisé pour développer votre activité.",
      supportTitle: "Accompagnement Complet",
      supportDesc: "Formation, marketing, logistique - nous vous accompagnons à chaque étape de votre réussite.",
      benefitsTitle: "Avantages Partenaire",
      studyTitle: "Étude de Marché & Assistance",
      studyDesc: "Bénéficiez d'une étude de marché commerciale complète, analyse d'occupation territoriale et assistance marketing personnalisée pour maximiser votre succès.",
      benefits: [
        { icon: TrendingUp, title: "Marges Attractives", desc: "Profitez de marges compétitives sur toute notre gamme" },
        { icon: Truck, title: "Livraison Gratuite", desc: "Livraison offerte à partir d'une commande minimum" },
        { icon: Gift, title: "Produits Promotionnels", desc: "Accès aux offres exclusives et promotions saisonnières" },
        { icon: Headphones, title: "Support Dédié", desc: "Un commercial dédié à votre service 7j/7" },
        { icon: BarChart3, title: "Étude de Marché", desc: "Analyse commerciale de votre zone de chalandise" },
        { icon: MapPin, title: "Occupation Territoriale", desc: "Stratégie d'implantation optimale pour votre région" },
        { icon: Megaphone, title: "Assistance Marketing", desc: "PLV, affiches, supports de vente personnalisés" },
        { icon: Target, title: "Formation Continue", desc: "Formation sur nos produits et techniques de vente" }
      ],
      formTitle: "Demandez votre Partenariat",
      formDesc: "Remplissez ce formulaire et notre équipe vous contactera sous 24h.",
      nameLabel: "Nom complet *",
      emailLabel: "Email *",
      phoneLabel: "Téléphone *",
      cityLabel: "Ville *",
      messageLabel: "Message",
      submitBtn: "Envoyer ma demande"
    },
    en: {
      badge: "PARTNERSHIP",
      title: "Become an Olion Partner",
      subtitle: "Join our network of 200+ partners across Tunisia",
      conceptTitle: "Partnership Concept",
      conceptDesc: "A win-win model with attractive margins and personalized support to grow your business.",
      supportTitle: "Complete Support",
      supportDesc: "Training, marketing, logistics - we support you at every step of your success.",
      benefitsTitle: "Partner Benefits",
      studyTitle: "Market Study & Assistance",
      studyDesc: "Benefit from a complete commercial market study, territorial occupation analysis and personalized marketing assistance to maximize your success.",
      benefits: [
        { icon: TrendingUp, title: "Attractive Margins", desc: "Enjoy competitive margins on our entire range" },
        { icon: Truck, title: "Free Delivery", desc: "Free delivery from a minimum order" },
        { icon: Gift, title: "Promotional Products", desc: "Access to exclusive offers and seasonal promotions" },
        { icon: Headphones, title: "Dedicated Support", desc: "A dedicated sales rep at your service 7/7" },
        { icon: BarChart3, title: "Market Study", desc: "Commercial analysis of your catchment area" },
        { icon: MapPin, title: "Territorial Coverage", desc: "Optimal location strategy for your region" },
        { icon: Megaphone, title: "Marketing Assistance", desc: "POS displays, posters, personalized sales materials" },
        { icon: Target, title: "Continuous Training", desc: "Training on our products and sales techniques" }
      ],
      formTitle: "Request your Partnership",
      formDesc: "Fill out this form and our team will contact you within 24 hours.",
      nameLabel: "Full name *",
      emailLabel: "Email *",
      phoneLabel: "Phone *",
      cityLabel: "City *",
      messageLabel: "Message",
      submitBtn: "Send my request"
    },
    ar: {
      badge: "شراكة",
      title: "كن شريك أوليون",
      subtitle: "انضم إلى شبكتنا من 200+ شريك عبر تونس",
      conceptTitle: "مفهوم الشراكة",
      conceptDesc: "نموذج مربح للجانبين مع هوامش جذابة ودعم شخصي لتنمية أعمالك.",
      supportTitle: "دعم كامل",
      supportDesc: "التدريب والتسويق واللوجستيات - نحن ندعمك في كل خطوة من نجاحك.",
      benefitsTitle: "مزايا الشريك",
      studyTitle: "دراسة السوق والمساعدة",
      studyDesc: "استفد من دراسة سوق تجارية كاملة وتحليل الاحتلال الإقليمي ومساعدة تسويقية مخصصة لتحقيق أقصى قدر من النجاح.",
      benefits: [
        { icon: TrendingUp, title: "هوامش جذابة", desc: "استمتع بهوامش تنافسية على مجموعتنا الكاملة" },
        { icon: Truck, title: "توصيل مجاني", desc: "توصيل مجاني من الحد الأدنى للطلب" },
        { icon: Gift, title: "منتجات ترويجية", desc: "الوصول إلى العروض الحصرية والعروض الموسمية" },
        { icon: Headphones, title: "دعم مخصص", desc: "مندوب مبيعات مخصص في خدمتك 7/7" },
        { icon: BarChart3, title: "دراسة السوق", desc: "تحليل تجاري لمنطقة جذبك" },
        { icon: MapPin, title: "التغطية الإقليمية", desc: "استراتيجية موقع مثالية لمنطقتك" },
        { icon: Megaphone, title: "مساعدة تسويقية", desc: "عروض نقاط البيع والملصقات ومواد البيع المخصصة" },
        { icon: Target, title: "تدريب مستمر", desc: "التدريب على منتجاتنا وتقنيات البيع" }
      ],
      formTitle: "اطلب شراكتك",
      formDesc: "املأ هذا النموذج وسيتصل بك فريقنا خلال 24 ساعة.",
      nameLabel: "الاسم الكامل *",
      emailLabel: "البريد الإلكتروني *",
      phoneLabel: "الهاتف *",
      cityLabel: "المدينة *",
      messageLabel: "الرسالة",
      submitBtn: "أرسل طلبي"
    }
  };

  const c = content[language] || content.fr;

  return (
    <section id="partenaires" className="py-20 bg-gradient-to-b from-white to-blue-50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className={`text-center max-w-3xl mx-auto mb-16 ${direction === 'rtl' ? 'text-right' : ''}`}>
          <span className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-4 py-2 rounded-full text-sm font-semibold">
            {c.badge}
          </span>
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mt-6 mb-4">
            {c.title}
          </h2>
          <p className="text-xl text-gray-600">
            {c.subtitle}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Left - Benefits */}
          <div className="space-y-8">
            {/* Concept & Support Cards */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl p-6 text-white shadow-2xl">
                <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center mb-4">
                  <Handshake className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold mb-2">{c.conceptTitle}</h3>
                <p className="text-blue-100 text-sm leading-relaxed">{c.conceptDesc}</p>
              </div>

              <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl p-6 text-white shadow-2xl">
                <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center mb-4">
                  <Truck className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold mb-2">{c.supportTitle}</h3>
                <p className="text-orange-100 text-sm leading-relaxed">{c.supportDesc}</p>
              </div>
            </div>

            {/* Market Study Card - NEW */}
            <div className="bg-gradient-to-br from-purple-600 to-purple-700 rounded-2xl p-6 text-white shadow-2xl">
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center flex-shrink-0">
                  <LineChart className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">{c.studyTitle}</h3>
                  <p className="text-purple-100 text-sm leading-relaxed">{c.studyDesc}</p>
                </div>
              </div>
            </div>

            {/* Benefits Grid */}
            <div className="bg-white rounded-2xl p-8 shadow-xl border border-gray-100">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">{c.benefitsTitle}</h3>
              <div className="grid grid-cols-2 gap-4">
                {c.benefits.map((benefit, index) => {
                  const IconComponent = benefit.icon;
                  return (
                    <div key={index} className="flex flex-col items-center text-center p-4 rounded-xl bg-gray-50 hover:bg-blue-50 transition-colors group">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform shadow-lg">
                        <IconComponent className="w-6 h-6 text-white" />
                      </div>
                      <h4 className="font-bold text-gray-900 text-sm mb-1">{benefit.title}</h4>
                      <p className="text-gray-500 text-xs leading-relaxed">{benefit.desc}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Right - Form */}
          <div className="bg-white rounded-2xl p-8 shadow-2xl border border-gray-100 h-fit sticky top-24">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">
              {c.formTitle}
            </h3>
            <p className="text-gray-600 mb-8">
              {c.formDesc}
            </p>

            {submitted ? (
              <div className="flex flex-col items-center justify-center py-12 space-y-4" data-testid="partner-success">
                <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center">
                  <CheckCircle2 className="w-12 h-12 text-green-600" />
                </div>
                <h4 className="text-2xl font-bold text-gray-900 text-center">
                  {language === 'ar' ? 'تم إرسال طلبك!' : language === 'en' ? 'Request sent!' : 'Demande envoyée !'}
                </h4>
                <p className="text-gray-600 text-center">
                  {language === 'ar' ? 'سيتصل بك فريقنا التجاري خلال 24 ساعة.' : language === 'en' ? 'Our commercial team will contact you within 24h.' : 'Notre équipe commerciale vous contactera sous 24h.'}
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {c.nameLabel}
                </label>
                <Input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Votre nom"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {c.emailLabel}
                  </label>
                  <Input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="votre@email.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {c.phoneLabel}
                  </label>
                  <Input
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="+216 XX XXX XXX"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {c.cityLabel}
                </label>
                <Input
                  type="text"
                  required
                  value={formData.city}
                  onChange={(e) => setFormData({...formData, city: e.target.value})}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Votre ville"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {c.messageLabel}
                </label>
                <Textarea
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  rows={3}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 resize-none"
                  placeholder="Parlez-nous de votre projet..."
                />
              </div>

              {errorMsg && (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="partner-error">
                  <AlertCircle className="w-5 h-5 flex-shrink-0" />
                  <span>{errorMsg}</span>
                </div>
              )}

              <Button
                type="submit"
                disabled={submitting}
                data-testid="partner-submit-btn"
                className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white py-5 rounded-xl text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 group disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? '…' : c.submitBtn}
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
